import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HelpCircle } from "lucide-react";

interface Term {
  term: string;
  definition: string;
}

const TERMS: Term[] = [
  {
    term: "TUPE",
    definition:
      "Transfer of Undertakings (Protection of Employment) Regulations 2006, the UK rules that protect employee rights when a business or service is transferred to a new employer.",
  },
  {
    term: "ACAS",
    definition:
      "The Advisory, Conciliation and Arbitration Service, the independent public body that helps resolve UK workplace disputes, including mandatory Early Conciliation before most tribunal claims.",
  },
  {
    term: "ERA 2025",
    definition:
      "The Employment Rights Act 2025, the recent overhaul of UK employment law introducing day-one rights (unfair dismissal, SSP, paternity leave), stricter fire-and-rehire rules and new fair-work obligations.",
  },
  {
    term: "CIPD",
    definition:
      "Chartered Institute of Personnel and Development, the UK professional body for HR practitioners. CIPD membership signals verified HR competence.",
  },
];

interface Props {
  variant?: "section" | "hero";
}

/** Compact glossary rendered near the bottom of every location article.
 *  Popover triggers give tooltip-like behaviour while staying keyboard
 *  accessible and readable to screen readers. */
export function LocationGlossary({ variant = "section" }: Props) {
  if (variant === "hero") {
    return (
      <div className="rounded-none border border-border/70 bg-background p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand">
          <span aria-hidden="true" className="h-px w-4 bg-accent" />
          Terms used on this page
        </h2>
        <p className="mt-3 text-xs text-muted-foreground">
          Tap any term for a plain-English definition.
        </p>
        <ul className="mt-6 space-y-5">
          {TERMS.map((t) => (
            <li key={t.term}>
              <Popover>
                <PopoverTrigger
                  aria-label={`Definition of ${t.term}`}
                  className="group block w-full text-left focus-visible:outline-none"
                >
                  <p className="text-[11px] font-bold uppercase tracking-wider text-accent group-hover:underline">
                    {t.term}
                    <HelpCircle className="ml-1 inline size-3 text-muted-foreground" aria-hidden="true" />
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                    {t.definition}
                  </p>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm text-sm leading-relaxed">
                  <p className="font-serif text-base text-brand">{t.term}</p>
                  <p className="mt-2 text-muted-foreground">{t.definition}</p>
                </PopoverContent>
              </Popover>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <section className="bg-background px-6 py-16 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-accent">
          Jargon buster
        </p>
        <h2 className="mt-3 font-serif text-2xl leading-tight text-brand sm:text-3xl">
          Terms used on this page
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Tap any term for a plain-English definition.
        </p>
        <ul className="mt-6 flex flex-wrap gap-3">
          {TERMS.map((t) => (
            <li key={t.term}>
              <Popover>
                <PopoverTrigger
                  aria-label={`Definition of ${t.term}`}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-brand transition hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <abbr title={t.definition} className="no-underline">
                    {t.term}
                  </abbr>
                  <HelpCircle className="size-3.5 text-muted-foreground" aria-hidden="true" />
                </PopoverTrigger>
                <PopoverContent className="max-w-sm text-sm leading-relaxed">
                  <p className="font-serif text-base text-brand">{t.term}</p>
                  <p className="mt-2 text-muted-foreground">{t.definition}</p>
                </PopoverContent>
              </Popover>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
